import React, { useState, useEffect } from 'react';
import { timesheetAPI, commesseAPI, risorseAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Check, 
  X,
  Loader2,
  Clock,
  Filter,
  CheckCircle2,
  XCircle
} from 'lucide-react';

export const TimesheetApprovalPage = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [risorse, setRisorse] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [filterCommessa, setFilterCommessa] = useState('all');
  const [filterRisorsa, setFilterRisorsa] = useState('all');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [entriesRes, commesseRes, risorseRes] = await Promise.all([
        timesheetAPI.getPendingApproval(),
        commesseAPI.getAll(),
        risorseAPI.getAll()
      ]);
      setEntries(entriesRes.data);
      setCommesse(commesseRes.data);
      setRisorse(risorseRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleEntry = (entryId) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const toggleAll = () => {
    if (selectedEntries.length === filteredEntries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(filteredEntries.map(e => e.id));
    }
  };

  const handleApprove = async () => {
    if (selectedEntries.length === 0) {
      toast.error('Seleziona almeno una registrazione');
      return;
    }

    setIsSubmitting(true);
    try {
      await timesheetAPI.approve({
        entry_ids: selectedEntries,
        azione: 'approva'
      });
      toast.success(`${selectedEntries.length} registrazioni approvate`);
      setSelectedEntries([]);
      loadData();
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Errore nell\'approvazione');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (selectedEntries.length === 0) {
      toast.error('Seleziona almeno una registrazione');
      return;
    }

    setIsSubmitting(true);
    try {
      await timesheetAPI.approve({
        entry_ids: selectedEntries,
        azione: 'respingi',
        motivo: rejectReason || 'Non specificato'
      });
      toast.success(`${selectedEntries.length} registrazioni respinte`);
      setSelectedEntries([]);
      setRejectDialogOpen(false);
      setRejectReason('');
      loadData();
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error('Errore nel rifiuto');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter entries
  const filteredEntries = entries.filter(entry => {
    if (filterCommessa !== 'all' && entry.commessa_id !== filterCommessa) return false;
    if (filterRisorsa !== 'all' && entry.risorsa_id !== filterRisorsa) return false;
    return true;
  });

  // Group entries by risorsa
  const groupedByRisorsa = filteredEntries.reduce((acc, entry) => {
    const key = entry.risorsa_id;
    if (!acc[key]) {
      acc[key] = {
        risorsa_nome: entry.risorsa_nome,
        entries: [],
        ore_totali: 0,
        costo_totale: 0
      };
    }
    acc[key].entries.push(entry);
    acc[key].ore_totali += entry.ore;
    acc[key].costo_totale += entry.costo_calcolato;
    return acc;
  }, {});

  // Get unique risorse from entries
  const uniqueRisorse = [...new Set(entries.map(e => e.risorsa_id))].map(id => {
    const entry = entries.find(e => e.risorsa_id === id);
    return { id, nome: entry?.risorsa_nome };
  });

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="approval-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timesheet-approval-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Approvazione Timesheet</h1>
          <p className="text-muted-foreground">
            {entries.length} registrazioni in attesa di approvazione
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setRejectDialogOpen(true)}
            disabled={selectedEntries.length === 0 || isSubmitting}
            data-testid="reject-selected-btn"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Respingi ({selectedEntries.length})
          </Button>
          <Button 
            onClick={handleApprove}
            disabled={selectedEntries.length === 0 || isSubmitting}
            data-testid="approve-selected-btn"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Approva ({selectedEntries.length})
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-2 flex-1">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={filterCommessa} onValueChange={setFilterCommessa}>
                <SelectTrigger className="w-full sm:w-64" data-testid="filter-commessa-select">
                  <SelectValue placeholder="Filtra per commessa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le commesse</SelectItem>
                  {commesse.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.codice_commessa} - {c.nome_progetto}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={filterRisorsa} onValueChange={setFilterRisorsa}>
                <SelectTrigger className="w-full sm:w-64" data-testid="filter-risorsa-select">
                  <SelectValue placeholder="Filtra per risorsa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le risorse</SelectItem>
                  {uniqueRisorse.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      {filteredEntries.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nessuna registrazione in attesa di approvazione</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedEntries.length === filteredEntries.length && filteredEntries.length > 0}
                      onCheckedChange={toggleAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Risorsa</TableHead>
                  <TableHead>Commessa</TableHead>
                  <TableHead>Fase</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Ore</TableHead>
                  <TableHead className="text-right">Costo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry) => (
                  <TableRow 
                    key={entry.id} 
                    className={selectedEntries.includes(entry.id) ? 'bg-muted/50' : ''}
                    data-testid={`approval-row-${entry.id}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedEntries.includes(entry.id)}
                        onCheckedChange={() => toggleEntry(entry.id)}
                        data-testid={`select-entry-${entry.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {new Date(entry.data).toLocaleDateString('it-IT', { 
                        weekday: 'short', 
                        day: 'numeric', 
                        month: 'short' 
                      })}
                    </TableCell>
                    <TableCell className="font-medium">{entry.risorsa_nome}</TableCell>
                    <TableCell>
                      <div>
                        <span className="font-mono text-xs text-muted-foreground">
                          {entry.commessa_codice}
                        </span>
                        <p className="text-sm">{entry.commessa_nome}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {entry.fase_nome || '-'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                      {entry.note || '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {entry.ore}h
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(entry.costo_calcolato)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Summary by Risorsa */}
      {Object.keys(groupedByRisorsa).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Riepilogo per Risorsa</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(groupedByRisorsa).map(([risorsaId, data]) => (
                <div 
                  key={risorsaId}
                  className="p-4 rounded-lg border border-border"
                >
                  <p className="font-medium mb-2">{data.risorsa_nome}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Registrazioni:</span>
                    <span className="font-mono">{data.entries.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Ore totali:</span>
                    <span className="font-mono font-semibold">{data.ore_totali}h</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Costo totale:</span>
                    <span className="font-mono">{formatCurrency(data.costo_totale)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Respingi Registrazioni</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Stai per respingere {selectedEntries.length} registrazione/i. 
              Inserisci un motivo per informare le risorse.
            </p>
            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo del Rifiuto</Label>
              <Textarea
                id="motivo"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Es. Ore non corrispondenti alle attività svolte..."
                rows={3}
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={isSubmitting}
              data-testid="confirm-reject-btn"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Respingi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimesheetApprovalPage;
